import React from "react";
import styled from "styled-components";
import { useRouter } from "next/router";
import { GetStaticPathsResult } from "next";

import Page from "shared/ui/Page";

const ASSETS_CDN = process.env.NEXT_PUBLIC_ASSETS_CDN;

export default function Explore({ geneses }: { geneses: any[] }): JSX.Element {
  const router = useRouter();

  // Get the Genesis index and combination definition strings from the path
  const [genesisIdxDef = "0", combinationDef = ""] = (router.query
    .genesisDefinition || []) as string[];

  // Parse definition strings into their correct types
  const genesisIdx = parseInt(genesisIdxDef as string);
  const combination = combinationDef.split("").map((x) => parseInt(x, 10));

  // Find the Genesis metadata
  const genesis = geneses.find((g) => g.genesisIdx === genesisIdx);

  // If a combination wasn't set in the path, set it to the initial generation
  React.useEffect(() => {
    if (!genesis || !!combination.length) return;

    router.push(
      `/explore/${genesisIdx}/${genesis.startingGeneration.join("")}`,
      undefined,
      { shallow: true }
    );
  }, [genesis]);

  const shiftGenesis = (offset: number) => {
    let newGenesisIdx = genesisIdx + offset;

    if (newGenesisIdx > 39) {
      newGenesisIdx -= 40;
    } else if (newGenesisIdx < 0) {
      newGenesisIdx += 40;
    }

    router.push(`/explore/${newGenesisIdx}/${combinationDef}`, undefined, {
      shallow: true,
    });
  };

  const shiftVariant = (layer: number, offset: number) => {
    const newCombination = [...combination];
    newCombination[layer] += offset;

    if (newCombination[layer] > 3) {
      newCombination[layer] -= 4;
    } else if (newCombination[layer] < 0) {
      newCombination[layer] += 4;
    }

    router.push(
      `/explore/${genesisIdx}/${newCombination.join("")}`,
      undefined,
      { shallow: true }
    );
  };

  const openseaMutagensLink = (layer: number, variant: number) => {
    const regularVariantCount =
      4 -
      (genesis.rareVariants.Moon[0] === layer ? 1 : 0) -
      (genesis.rareVariants.Punk[0] === layer ? 1 : 0);

    const variantNumbers = new Array(100)
      .fill(null)
      .map((_, i) => i)
      .filter((_, i) => i % regularVariantCount === variant);

    const variantsQuery = variantNumbers
      .map((variant, i) => `search[stringTraits][0][values][${i}]=${variant}`)
      .join("&");

    return `https://opensea.io/collection/mutagen?collectionSlug=mutagen&search[sortAscending]=true&search[sortBy]=PRICE&search[stringTraits][0][name]=Variant%20number&${variantsQuery}&search[stringTraits][1][name]=Layer&search[stringTraits][1][values][0]=${layer}&search[toggles][0]=BUY_NOW`;
  };

  const rareVariant = (layer: number, variant: number) => {
    if (
      genesis.rareVariants.Moon[0] === layer &&
      genesis.rareVariants.Moon[1] === variant
    ) {
      return "Moon";
    }

    if (
      genesis.rareVariants.Punk[0] === layer &&
      genesis.rareVariants.Punk[1] === variant
    ) {
      return "Punk";
    }
  };

  const renderLayerControls = (layer: number) => {
    const rareVariantName = rareVariant(layer, combination[layer]);

    return (
      <p>
        <h3>
          Layer {layer} - variant {combination[layer]}{" "}
          {rareVariantName ? `(${rareVariantName})` : ""}
        </h3>

        <LayerControlLinks>
          {!rareVariantName ? (
            <a
              target="_blank"
              href={openseaMutagensLink(layer, combination[layer])}
            >
              Get Mutagens for this mutation
            </a>
          ) : (
            ""
          )}
        </LayerControlLinks>

        <LayerControlLinks>
          <a onClick={() => shiftVariant(layer, -1)}>Previous variant</a>
          <a onClick={() => shiftVariant(layer, 1)}>Next variant</a>
        </LayerControlLinks>
      </p>
    );
  };

  return (
    <Page>
      <Contents>
        <Details>
          <GenesisNavigation>
            <a onClick={() => shiftGenesis(-1)}>Previous Genesis</a>
            <a onClick={() => shiftGenesis(1)}>Next Genesis</a>
          </GenesisNavigation>

          <Header>
            <ArtistName>{genesis?.traits.Artist}</ArtistName>

            <GenesisName>
              #{genesis?.genesisIdx} {genesis?.title}
            </GenesisName>
          </Header>

          <LayerControls>
            {renderLayerControls(0)}
            {renderLayerControls(1)}
            {renderLayerControls(2)}
            {renderLayerControls(3)}
          </LayerControls>
        </Details>

        <ImageContainer>
          <img
            src={`${ASSETS_CDN}/genesis/${genesis?.genesisIdx}/l0v${combination[0]}.png`}
            width={697}
            height={984}
          />
          <img
            src={`${ASSETS_CDN}/genesis/${genesis?.genesisIdx}/l1v${combination[1]}.png`}
            width={697}
            height={984}
          />
          <img
            src={`${ASSETS_CDN}/genesis/${genesis?.genesisIdx}/l2v${combination[2]}.png`}
            width={697}
            height={984}
          />
          <img
            src={`${ASSETS_CDN}/genesis/${genesis?.genesisIdx}/l3v${combination[3]}.png`}
            width={697}
            height={984}
          />
        </ImageContainer>
      </Contents>
    </Page>
  );
}

export async function getStaticProps() {
  const geneses = await fetch(`${ASSETS_CDN}/genesis/metadata.json`).then(
    (data) => data.json()
  );

  return {
    props: {
      geneses,
    },
    revalidate: 60,
  };
}

export async function getStaticPaths(): Promise<GetStaticPathsResult> {
  return {
    paths: [], // Indicates that no page needs be created at build time
    fallback: "blocking", // Indicates the type of fallback
  };
}

const ImageContainer = styled.div`
  width: 100%;
  position: relative;

  img {
    position: absolute;
    top: 0;
    left: 0;
    border: 1px solid var(--color-black) !important;
  }
`;

const Contents = styled.div`
  margin-top: 1rem;
  display: flex;
  align-items: flex-start;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
`;

const Details = styled.div`
  height: 100%;
  width: 50%;
  display: flex;
  flex-direction: column;
  margin-right: var(--gutter);
`;

const ArtistName = styled.a`
  margin-bottom: 0.1rem;
  font-size: 2.1875rem;
  line-height: 145%;
  font-weight: bold;
  text-decoration: none;

  &:hover {
    text-decoration: none;
    color: var(--color-black);
  }
`;

const GenesisName = styled.h1`
  font-weight: bold;
  font-size: 2.1875rem;
  line-height: 145%;
  color: transparent;
  -webkit-text-stroke: 1px black;
  text-transform: uppercase;
  margin: 0;
`;

const LayerControlLinks = styled.div`
  width: 300px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;

  font-family: "SomeType Mono";
  text-transform: uppercase;

  a {
    cursor: pointer;
    margin: 4px 0;
  }
`;

const GenesisNavigation = styled.div`
  width: 300px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;

  margin-bottom: 20px;
  margin-top: 0;

  font-family: "SomeType Mono";
  text-transform: uppercase;

  a {
    cursor: pointer;
    margin: 10px 0;
  }
`;

const Header = styled.div`
  margin-bottom: 20px;
`;

const LayerControls = styled.div`
  margin: 20px 0;

  p {
    margin: 40px 0;
  }

  h3 {
    font-family: "SomeType Mono";
    text-transform: uppercase;
    margin: 10px 0;
  }
`;
